/**
 * Law 9's name filter, as the write lane's own second reading of it.
 *
 * The planner already refuses a prohibited name in any request key or string value before a plan
 * can exist (`EpisodeScriptService/plan.ts:73-89`, `:182-201`). This file is the independent
 * re-check the preflight runs on the plan it was handed, so that a request that reached the write
 * lane by any other route is still refused before a body is built.
 *
 * The table below is the FILTER, not product text — the same treatment, and the same cspell
 * wrapping, the ported planner gives its own copy. A note for whoever re-runs gate check G9: this
 * file is a filter region and must be scoped out of that grep exactly as `plan.ts:76-82` is.
 * Nothing here is ever rendered, logged or recorded: the callers report only which FIELD refused,
 * never the word that matched.
 *
 * The two normalising steps are the planner's, and they are load-bearing (BAR-QA-09): the scan runs
 * on an NFKC copy with the invisible characters stripped, so a full-width or zero-width-sprinkled
 * spelling cannot walk past, and a camelCase or snake_case key is split into the words it contains
 * before it is tested. The authored string itself is never touched.
 */

/* cspell:disable */
const PROHIBITED_TEXT: readonly RegExp[] = [
  /\bai\b/i,
  /\bmodel\b/i,
  /\bopenai\b/i,
  /\bchatgpt\b/i,
  /\bgpt(?:-?\d[\w.-]*)?\b/i,
  /\bclaude\b/i,
  /\banthropic\b/i,
  /\bgemini\b/i,
  /\bllama\b/i,
  /\bazure\b/i,
  /\bmicrosoft\b/i,
  /\bgoogle\b/i,
  /\bamazon\b/i,
  /\baws\b/i,
  /\bdorian\b/i,
];
/* cspell:enable */

// eslint-disable-next-line max-len, no-misleading-character-class -- the planner's expression, verbatim: the characters it strips include combining ones
const INVISIBLE = /[\u00AD\u034F\u061C\u115F\u1160\u17B4\u17B5\u180B-\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\u3164\uFE00-\uFE0F\uFEFF\uFFA0]/gu;

function scannable(text: unknown): string {
  return String(text).normalize('NFKC').replace(INVISIBLE, '');
}

/** A key read as the words it contains, so a prohibited term cannot hide inside an identifier. */
export function spacedKey(key: string): string {
  return scannable(key).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_\-.]+/g, ' ');
}

/** True when `text` names an AI, model, assistant or vendor the ship laws keep out of a request. */
export function containsProhibitedName(text: unknown): boolean {
  const subject = scannable(text);

  return PROHIBITED_TEXT.some((pattern) => pattern.test(subject));
}
